import React, { useState } from 'react';
import { signInWithPopup, GoogleAuthProvider, getAuth } from 'firebase/auth';
import { auth } from '../firebase/config';
import { Button, Typography, Box, CircularProgress, Alert } from '@mui/material';
import { Google as GoogleIcon, MoneyOff } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { designTokens } from '../theme/designTokens';

interface LoginProps {
  setUser: (user: any | null) => void;
}

const Login: React.FC<LoginProps> = ({ setUser }) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      const result = await signInWithPopup(auth, provider);
      setUser(result.user);
    } catch (error) {
      console.error('Error during Google login:', error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: designTokens.colors.primary.gradient,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-50%',
          right: '-50%',
          width: '100%',
          height: '100%',
          background: 'radial-gradient(circle, rgba(124, 58, 237, 0.3) 0%, transparent 70%)',
          animation: 'pulse 8s ease-in-out infinite',
        },
        '@keyframes pulse': {
          '0%, 100%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(1.1)' },
        },
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Box
          sx={{
            background: designTokens.glassmorphism.background,
            backdropFilter: designTokens.glassmorphism.backdropFilter,
            border: designTokens.glassmorphism.border,
            boxShadow: designTokens.glassmorphism.boxShadow,
            borderRadius: designTokens.borderRadius['2xl'],
            padding: { xs: 4, sm: 6 },
            maxWidth: '440px',
            width: '90vw',
            textAlign: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {/* Logo/Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                margin: '0 auto 24px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(59, 130, 246, 0.4)',
              }}
            >
              <MoneyOff sx={{ fontSize: 40, color: 'white' }} />
            </Box>
          </motion.div>

          {/* Title */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: designTokens.typography.fontWeight.bold,
              color: 'white',
              marginBottom: 1,
              fontSize: { xs: '1.75rem', sm: '2rem' },
            }}
          >
            Expense Tracker
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: designTokens.colors.text.secondary,
              marginBottom: 4,
              fontSize: designTokens.typography.fontSize.base,
            }}
          >
            Welcome! Please login to your account
          </Typography>

          {/* Error Alert */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Alert
                severity="error"
                sx={{
                  marginBottom: 3,
                  borderRadius: designTokens.borderRadius.md,
                  background: 'rgba(239, 68, 68, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                }}
              >
                {error}
              </Alert>
            </motion.div>
          )}

          {/* Google Sign In Button */}
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleGoogleLogin}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <GoogleIcon />}
            sx={{
              background: 'white',
              color: designTokens.colors.background.paper,
              padding: '12px 24px',
              fontSize: designTokens.typography.fontSize.base,
              fontWeight: designTokens.typography.fontWeight.medium,
              borderRadius: designTokens.borderRadius.lg,
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              transition: `all ${designTokens.transitions.normal}`,
              '&:hover': {
                background: '#f9fafb',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
              '&:disabled': {
                background: 'rgba(255, 255, 255, 0.5)',
                color: designTokens.colors.text.disabled,
              },
            }}
          >
            {isLoading ? 'Signing in...' : 'Sign in with Google'}
          </Button>

          {/* Footer Text */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              marginTop: 3,
              color: designTokens.colors.text.secondary,
              fontSize: designTokens.typography.fontSize.sm,
            }}
          >
            Secure authentication powered by Google
          </Typography>
        </Box>
      </motion.div>
    </Box>
  );
};

export default Login;